import React, { useState, useEffect, useRef } from 'react';
import { getSupabase } from './supabase';
import moment from 'moment';
import VehicleDetails from './VehicleDetails';
import LoadingSpinner from './LoadingSpinner';
import { Buffer } from 'buffer';
import './App.css';
import { IoIosAdd } from 'react-icons/io';
import { IoQrCodeOutline } from "react-icons/io5";
import { IoClose } from 'react-icons/io5';
import { IoArrowUp } from 'react-icons/io5';
import { Html5Qrcode } from 'html5-qrcode';

// Добавить Buffer для совместимости
window.Buffer = window.Buffer || Buffer;

const vehicleTypes = ['Fura', 'Kamaz', 'Stoyanka', 'Gazel'];
const rates = { 'Fura': 15000, 'Kamaz': 10000, 'Stoyanka': 15000, 'Gazel': 5000 };

const STOYANKA_RATE = 15000;
const STOYANKA_FREE_DAYS = 3;

// Генерация ключа из пароля
async function deriveKey(password, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function decryptAES(encoded) {
  const combined = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const ciphertext = combined.slice(28);

  const key = await deriveKey('eet gavno', salt);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv }, key, ciphertext
  );

  return new TextDecoder().decode(decrypted);
}

const IMAGEKIT_PUBLIC_KEY = process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY;
const IMAGEKIT_PRIVATE_KEY = process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY;

const uploadImage = async (imageBlob, vehicleId) => {
  try {
    const fileName = `photos/${vehicleId}.jpg`;
    
    const formData = new FormData();
    formData.append('file', imageBlob, fileName);
    formData.append('fileName', fileName);
    formData.append('publicKey', await decryptAES(IMAGEKIT_PUBLIC_KEY));
    formData.append('folder', '/vehicles');

    const response = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
      method: 'POST',
      headers: {
        Authorization: 'Basic ' + btoa(`${await decryptAES(IMAGEKIT_PRIVATE_KEY)}:`),
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) throw new Error(result.message || 'ImageKit upload failed');
    return { url: result.url.split('?')[0], fileId: result.fileId };
  } catch (error) {
    console.error('Ошибка при загрузке изображения:', error.message);
    throw new Error('Ошибка загрузки фото: ' + error.message);
  }
};

const toggleModal = () => { 
  document.querySelector('body').classList.toggle('modalopen');
};

const calculatePrice = (type, entryDate, exitDate) => {
  const entry = new Date(entryDate);
  const end = exitDate ? new Date(exitDate) : new Date();
  const days = Math.max(1, Math.ceil((end - entry) / (1000 * 60 * 60 * 24)));

  if (type === 'Stoyanka') {
    const chargeableDays = 1 + Math.max(0, days - (STOYANKA_FREE_DAYS));
    return { days, rate: STOYANKA_RATE, price: chargeableDays * STOYANKA_RATE };
  }

  if (type === 'Fura') {
    const FURA_FIRST_DAY_RATE = 15000;
    const FURA_NEXT_DAY_RATE = 25000;
    const price = FURA_FIRST_DAY_RATE + Math.max(0, days - 1) * FURA_NEXT_DAY_RATE;
    // rate тут условный — берём ставку последнего дня для отображения
    const rate = days > 1 ? FURA_NEXT_DAY_RATE : FURA_FIRST_DAY_RATE;
    return { days, rate, price };
  }

  const rate = rates[type];
  return { days, rate, price: days * rate };
};

function MainScreen({ filter, setFilter }) {
  const [vehicles, setVehicles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [type, setType] = useState('');
  const [number, setNumber] = useState('');
  const [showExitScanner, setShowExitScanner] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showAddNumberModal, setShowAddNumberModal] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [carNumber, setCarNumber] = useState('');
  const [showFullScreenImage, setShowFullScreenImage] = useState(null);
  const [showPrintPrompt, setShowPrintPrompt] = useState(false);
  const [isPayed, setIsPayed] = useState(true);
  const [pendingVehicle, setPendingVehicle] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const inputRef = useRef(null);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [dateFilter, setDateFilter] = useState('Бугунги');
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef({ code: null, timestamp: 0 });

  useEffect(() => {
    fetchVehicles();
  }, [filter, dateFilter, selectedDate]);

  useEffect(() => {
    if (!photo) {
      setPhotoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  useEffect(() => {
    const handleScroll = () => {
      const buttonsContainer = document.querySelector('.buttons');
      if (buttonsContainer) {
        const rect = buttonsContainer.getBoundingClientRect();
        const isButtonsVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
        setShowScrollTopButton(!isButtonsVisible && window.scrollY > 100);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (scannerRef.current) {
        scannerRef.current.clear().catch(err => console.error('Ошибка очистки сканера:', err));
        scannerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (showExitScanner && inputRef.current) {
      inputRef.current.focus();
    }
    if (showExitScanner) {
      toggleModal();
      let html5QrCode = new Html5Qrcode("qr-reader");

      const startScanner = async () => {
        try {
          const devices = await Html5Qrcode.getCameras();
          if (devices && devices.length > 0) {
            const backCamera = devices.find(device =>
              device.label.toLowerCase().includes('back') ||
              device.label.toLowerCase().includes('rear') ||
              device.label.toLowerCase().includes('environment')
            );

            const config = {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              rememberLastUsedCamera: true,
              videoConstraints: backCamera 
                ? { 
                    deviceId: { exact: backCamera.id },
                    facingMode: { exact: 'environment' }
                  }
                : { facingMode: { exact: 'environment' } }
            };

            await html5QrCode.start(
              backCamera ? { deviceId: { exact: backCamera.id } } : { facingMode: 'environment' },
              config,
              (result) => {
                const now = Date.now();
                const lastScanned = lastScannedRef.current;
                if (lastScanned.code === result && now - lastScanned.timestamp < 3000) {
                  return;
                }
                lastScannedRef.current = { code: result, timestamp: now };
                handleScan(result, true);
              },
              (error) => {
                console.error('Ошибка сканирования:', error);
              }
            );
          } else {
            const config = {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              rememberLastUsedCamera: true,
              videoConstraints: { facingMode: { exact: 'environment' } }
            };

            await html5QrCode.start(
              { facingMode: 'environment' },
              config,
              (result) => {
                const now = Date.now();
                const lastScanned = lastScannedRef.current;
                if (lastScanned.code === result && now - lastScanned.timestamp < 3000) {
                  return;
                }
                lastScannedRef.current = { code: result, timestamp: now };
                handleScan(result, true);
              },
              (error) => {
                console.error('Ошибка сканирования:', error);
                if (error && error.name === 'NotAllowedError') {
                  alert('Kameraga ruxsat bering!');
                } else {
                  alert('Kamerada xato: ' + (error ? error.message : 'Noma\'lum xato'));
                }
              }
            );
          }
          scannerRef.current = html5QrCode;
        } catch (err) {
          console.error('Ошибка инициализации сканера:', err);
          let html5QrCodeFallback = new Html5Qrcode("qr-reader");
          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            rememberLastUsedCamera: true,
            videoConstraints: { facingMode: { exact: 'environment' } }
          };

          try {
            await html5QrCodeFallback.start(
              { facingMode: 'environment' },
              config,
              (result) => {
                const now = Date.now();
                const lastScanned = lastScannedRef.current;
                if (lastScanned.code === result && now - lastScanned.timestamp < 3000) {
                  return;
                }
                lastScannedRef.current = { code: result, timestamp: now };
                handleScan(result, true);
              },
              (error) => {
                console.error('Ошибка сканирования (fallback):', error);
                if (error && error.name === 'NotAllowedError') {
                  alert('Kameraga ruxsat bering!');
                } else {
                  alert('Kamerada xato: ' + (error ? error.message : 'Noma\'lum xato'));
                }
              }
            );
            scannerRef.current = html5QrCodeFallback;
          } catch (fallbackErr) {
            console.error('Fallback не сработал:', fallbackErr);
            document.getElementById('qr-label').style.display = 'none';
          }
        }
      };

      startScanner();

      const hideFrontCameras = setInterval(() => {
        const selectElement = document.getElementById('html5-qrcode-select-camera');
        if (selectElement) {
          document.querySelectorAll("#html5-qrcode-select-camera option").forEach(option => {
            if (option.textContent.toLowerCase().includes("front")) {
              option.remove();
            }
          });
          selectElement.selectedIndex = 0;
          clearInterval(hideFrontCameras);
        }
      }, 500);
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().then(() => {
          scannerRef.current.clear().catch(err => console.error('Ошибка очистки сканера:', err));
        }).catch(err => console.error('Ошибка остановки сканера:', err));
        scannerRef.current = null;
      }
    };
  }, [showExitScanner]);


  const fetchVehicles = async () => {
    setIsLoading(true);
    const supabase = await getSupabase();
    let query = supabase.from('vehicles').select('*');
    
    if (dateFilter === 'Бугунги') {
      const startOfDay = moment().startOf('day').toISOString();
      const endOfDay = moment().endOf('day').toISOString();
      query = query.or(
        `and(status.eq.Turibti,entry_date.gte.${startOfDay},entry_date.lte.${endOfDay}),and(status.eq.Chiqdi,exit_date.gte.${startOfDay},exit_date.lte.${endOfDay}),and(status.eq."To'lanmadi",exit_date.gte.${startOfDay},exit_date.lte.${endOfDay})`
      );
    } else if (dateFilter === 'Бошка сана' && selectedDate) {
      const startOfDay = moment(selectedDate).startOf('day').toISOString();
      const endOfDay = moment(selectedDate).endOf('day').toISOString();
      query = query.or(
        `and(status.eq.Turibti,entry_date.gte.${startOfDay},entry_date.lte.${endOfDay}),and(status.eq.Chiqdi,exit_date.gte.${startOfDay},exit_date.lte.${endOfDay}),and(status.eq."To'lanmadi",exit_date.gte.${startOfDay},exit_date.lte.${endOfDay})`
      );
    }

    if (filter === 'Raqamsiz') query = query.eq('number', '');
    if (filter === 'Kirdi') query = query.eq('status', 'Turibti');
    if (filter === 'Chiqdi') query = query.eq('status', 'Chiqdi');
    if (filter === "To'lanmadi") query = query.eq('status', "To'lanmadi");
    if (filter === "Stoyanka") query = query.eq('type', "Stoyanka");


    query = query.order('entry_date', { ascending: false });

    const { data, error } = await query;
    if (error) console.error('Ошибка получения данных:', error);
    else setVehicles(data);
    setIsLoading(false);
  };

  const addVehicle = async () => {
    if (!type) return alert('Moshina turini tanlang');
    const entryDate = new Date();
    const vehicleId = `${entryDate.getTime()}${number ? `-${number}` : ''}`;
    const supabase = await getSupabase();

    const vehicleData = {
      vehicle_id: vehicleId,
      photo_url: '',
      entry_date: entryDate.toISOString(),
      type,
      number,
      status: 'Turibti',
      exit_date: null,
      paid_amount: 0,
      comment: '' // Добавляем пустой комментарий по умолчанию
    };

    const { data: insertedData, error: insertError } = await supabase.from('vehicles').insert(vehicleData).select();

    if (insertError) {
      console.error('Ошибка добавления машины:', insertError);
      alert('Ошибка добавления машины в Supabase.');
      return;
    }

    setShowAddModal(false);
    toggleModal();

    if (photo) {
      uploadImage(photo, vehicleId)
        .then(async (response) => {
          const photoUrl = response.url;
          const { error: updateError } = await supabase
            .from('vehicles')
            .update({ photo_url: photoUrl })
            .eq('id', insertedData[0].id);

          if (updateError) {
            console.error('Ошибка обновления photo_url:', updateError);
          }
        })
        .catch((error) => {
          console.error('Ошибка загрузки фото в ImageKit:', error);
        });
    }

    setPendingVehicle({...vehicleData, id: insertedData[0].id});
    setShowPrintPrompt(true);

    setPhoto(null);
    setType('');
    setNumber('');
    setShowCamera(false);
  };

  const handlePrintDecision = (shouldPrint) => {
    if (shouldPrint && pendingVehicle) {
      printReceipt(pendingVehicle);
    }
    setPendingVehicle(null);
    setShowPrintPrompt(false);
    fetchVehicles();
  };

  const printReceipt = (data) => {
    const { rate } = calculatePrice(data.type, data.entry_date, null);
    const printData = {
      command: 'PRINT',
      vehicle_id: data.vehicle_id,
      entry_date: moment(data.entry_date).format('DD.MM.YYYY HH:mm'),
      number: data.number,
      type: data.type,
      rates: rates
    };
    if (window.Android) {
      window.Android.print(JSON.stringify(printData));
    } else {
      const printWindow = window.open('', '', 'height=500,width=300');
      printWindow.document.write(`
        <html>
          <head>
            <title>Chipta</title>
            <style>@media print { body { width: 58mm; font-size: 12pt; } #qr { margin-top: 10px; } }</style>
          </head>
          <body style="padding: 5px; display: flex; flex-direction: column; align-items: center;">
            <p style="margin: 0px; margin-top: 12px; font-weight: bold;">ID: ${data.vehicle_id}</p>
            <p style="margin: 0px; margin-top: 12px; font-weight: bold;">KIRISH: ${moment(data.entry_date).format('DD.MM.YYYY HH:mm')}</p>
            <p style="margin: 0px; margin-top: 12px; font-weight: bold;">MOSHINA RAQAMI: ${data.number || 'Kiritilmagan'}</p>
            <p style="margin: 0px; margin-top: 12px; font-weight: bold;">TURI: ${data.type}</p>
            <p style="margin: 0px; margin-top: 12px; font-weight: bold;">KUNIGA: ${rate} som</p>
            <div id="qr" style="margin-top: 12px;"></div>
          </body>
          <script> window.onload = ()=>{ setTimeout(window.close, 5000); }; </script>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleScan = async (result, isNum) => {
    if (result) {
      const cleanedResult = result;
      const supabase = await getSupabase();

      if (!cleanedResult) {
        alert('Boshidan harakat qilib koring!');
        return;
      }

      let vehicle;
      let error;

      if (isNum) {
        const { data, error: fetchError } = await supabase
          .from('vehicles')
          .select('*')
          .eq('vehicle_id', cleanedResult)
          .single();

        vehicle = data;
        error = fetchError;
      } else {
        const { data, error: fetchError } = await supabase
          .from('vehicles')
          .select('*')
          .eq('number', cleanedResult)
          .single();

        vehicle = data;
        error = fetchError;
      }

      if (error || !vehicle) {
        console.error('Ошибка запроса к Supabase:', error);
        alert(`Shu raqam ${cleanedResult} bilan moshina topilmadi yoki u allaqachon chiqib ketgan.`);
        return;
      }

      if (vehicle.status === 'Chiqdi' || vehicle.status === "To'lanmadi") {
        alert(`Bu moshina (ID: ${cleanedResult}) allaqachon chiqib ketgan.`);
        return;
      }

      const { days, price } = calculatePrice(vehicle.type, vehicle.entry_date, null);

      setPaymentDetails({
        vehicleId: vehicle.vehicle_id,
        number: vehicle.number || 'Kiritilmagan',
        type: vehicle.type,
        entryDate: vehicle.entry_date,
        days,
        price,
        vehicleIdInDb: vehicle.id,
        comment: '' // Инициализируем пустой комментарий
      });

      setIsPayed(true);

      setShowExitScanner(false);
      setShowPaymentModal(true);
    } else {
      alert('Qayta harakat qilib koring.');
    }
  };

  const confirmPayment = async () => {
    if (!paymentDetails) return;
    const supabase = await getSupabase();
    const now = new Date();
    const { error: updateError } = await supabase
      .from('vehicles')
      .update({
        status: isPayed ? 'Chiqdi' : "To'lanmadi",
        exit_date: now.toISOString(),
        paid_amount: paymentDetails.price,
        comment: paymentDetails.comment // Добавляем комментарий в обновление
      })
      .eq('id', paymentDetails.vehicleIdInDb);
    if (updateError) {
      console.error('Ошибка обновления статуса:', updateError);
      alert('Ошибка при обновления статуса машины.');
      return;
    }
    setShowPaymentModal(false);
    setPaymentDetails(null);
    fetchVehicles();
  };

  const addNumber = async () => {
    const supabase = await getSupabase();
    await supabase
      .from('vehicles')
      .update({ number: newNumber })
      .eq('id', selectedVehicle.id);
    setShowAddNumberModal(false);
    fetchVehicles();
  };

  const capturePhoto = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return;
    }

    if (!window.isSecureContext) {
      return;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setShowCamera(true);

    const startStream = async () => {
      if (!videoRef.current) {
        setTimeout(startStream, 100);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (videoRef.current) {
            await videoRef.current.play();
          }
        }
      } catch (err) {
        console.error('Ошибка доступа к камере:', err.name, err.message);
        setShowCamera(false);
        if (err.name === 'NotAllowedError') {
          alert('Kameraga ruxsat bloklangan. Brauzeringiz yoki qurilmangiz sozlamalarida kamerani ruxsat bering.');
        } else if (err.name === 'NotFoundError') {
          alert('Qurilmada kamera topilmadi');
        } else {
          alert(`Kamerada hato: ${err.message}. Boshqa brauzer yoki qurilmada harakat qilib koring`);
        }
      }
    };

    startStream();
  };

  const takePhoto = () => {
    if (!videoRef.current) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      setPhoto(blob);
      setShowCamera(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }, 'image/jpeg', 0.9);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeCamera = () => {
    setShowCamera(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  return (
    <div className="container">
      {isLoading && <LoadingSpinner />}
      <div className="filters">
        {['Hamma', 'Stoyanka', 'Kirdi', 'Chiqdi', "To'lanmadi"].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={(filter === f) ? 'active' : ''}>
            {f}
          </button>
        ))}
        <div style={{ marginLeft: 10 }}></div>
      </div>

      <div style={{ marginTop: 70 }}></div>

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column' }}>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{ margin: 10, padding: 10, width: '90%', border: "1px solid #bbbbbb", borderRadius: 10, fontSize: 18 }}
        >
          <option value="Бугунги">Бугунги</option>
          <option value="Бошка сана">Бошка сана</option>
          <option value="Хамма">Хамма</option>
        </select>
        {dateFilter === 'Бошка сана' && (
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ margin: 10, padding: 10, width: '90%', border: "1px solid #bbbbbb", borderRadius: 10, fontSize: 18 }}
          />
        )}
      </div>

      <div className="buttons">
        <button className="addCar" onClick={() => {setShowAddModal(true); toggleModal(); }}>
          <IoIosAdd /> Qo'shish
        </button>
        <button className="scanCar" onClick={() => {setCarNumber(''); setShowExitScanner(true);}}>
          <IoQrCodeOutline /> Chiqarish
        </button>
      </div>
      
      <ul className="vehicle-list">
        {
          vehicles.length > 0 ? vehicles.map(v => {

          const exitForCalc = v.status === 'Chiqdi' ? v.exit_date : null;
          const { price } = calculatePrice(v.type, v.entry_date, exitForCalc);
          
          const start = moment(v.entry_date).format('DD.MM.YYYY HH:mm');
          const exit = moment(v.exit_date).format('DD.MM.YYYY HH:mm');
          
          return (
            <li key={v.id} className="vehicle-card">
              <div className="vehicle-info" onClick={() => {setSelectedVehicle(v); toggleModal(); }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} >
                  <span className="vehicle-type" style={{ fontSize: 22 }}>{v.type}</span>
                  <span className="vehicle-status" style={{ backgroundColor: v.status === 'Chiqdi' ? 'green' : 'orange', paddingTop: 3, paddingBottom: 3, paddingLeft: 9, paddingRight: 9, fontSize: 18, color: 'white', borderRadius: 5, }}>{v.status}</span>
                </div>
                  <span className="vehicle-price" style={{ color: v.status === 'Chiqdi' ? 'green' : 'orange', fontSize: 20 }}>{price} so'm {v.status === 'Turibti' ? 'berish kerak' : "to'langan"}</span>
                <span className="vehicle-number" style={{ fontSize: 18 }}>{v.number || 'Raqamsiz'}</span>
                <span className="vehicle-entry-date" style={{ fontSize: 20 }}>Kirgan: {start}</span>
                {v.status === 'Chiqdi' && (
                  <span className="vehicle-exit-date" style={{ fontSize: 20 }}>Chiqgan: {exit}</span>
                )}
                <span className="vehicle-type" style={{ fontSize: 13 }}>ID: {v.vehicle_id}</span>
                <span className="vehicle-comment" style={{ fontSize: 16 }}>Kommentariy: {v.comment || 'Yo\'q'}</span> {/* Добавляем отображение комментария */}
              </div>
              {!v.number && (
                <button onClick={() => {setSelectedVehicle(v); setShowAddNumberModal(true);}} style={{ width: '100%', marginTop: 10 }}>
                  Raqam qo'shish
                </button>
              )}
            </li>
          );
        }) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontSize: '1.3em' }} > Hech narsa topilmadi </p>
          </div>
        )}
        <div style={{ marginTop: 70 }}></div>
      </ul>

      {showAddModal && (
        <div style={{ padding: 5, backgroundColor: '#000000aa', position: 'fixed', top: 0, left: 0, bottom: 0, right: 0, zIndex: 11 }}>
          <div className="modal" style={{ overflowY: 'scroll', maxHeight: '90%', scrollbarWidth: 'none' }}>
            <h2>Moshina qo'shish</h2>
            {showCamera ? (
              <div className="camera-container">
                <video ref={videoRef} className="camera-video" />
                <div className="modal-buttons">
                  <button onClick={takePhoto}>Rasmga olish</button>
                  <button className="cancel" onClick={closeCamera}>Bekor qilish</button>
                </div>
              </div>
            ) : (
              <div className="add-vehicle-form">
                <button onClick={capturePhoto}>Rasm qo'shish</button>
                {photo && photoPreviewUrl && (
                  <img 
                    src={photoPreviewUrl} 
                    alt="Foto" 
                    style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain', cursor: 'pointer' }} 
                    onClick={() => setShowFullScreenImage(photoPreviewUrl)}
                  />
                )}
                <select value={type} onChange={e => setType(e.target.value)}>
                  <option value="">Moshina turini tanlang</option>
                  {vehicleTypes.map(t => <option key={t}>{t}</option>)}
                </select>
                <input value={number} onChange={e => setNumber(e.target.value)} placeholder="Moshina raqamini kiritng" />
                <div className="modal-buttons">
                  <button onClick={() => {addVehicle();}} disabled={isLoading}>
                    {isLoading ? 'Saqlanmoqda...' : 'Saqlash'}
                  </button>
                  <button className="cancel" onClick={() => {
                    setShowAddModal(false);
                    toggleModal();
                    setShowCamera(false);
                    setPhoto(null);
                    if (streamRef.current) {
                      streamRef.current.getTracks().forEach(track => track.stop());
                      streamRef.current = null;
                    }
                  }}>Bekor qilish</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showExitScanner && (
        <div style={{ padding: 5, backgroundColor: '#000000aa', position: 'fixed', top: 0, left: 0, bottom: 0, right: 0, zIndex: 11 }}>
          <div className="modal scanner-container" style={{ overflowY: 'scroll', maxHeight: '90%', scrollbarWidth: 'none' }}>
            <button
              style={{ background: "none", color: "black", fontSize: 24, position: "absolute", right: "2%" }}
              onClick={() => {
                setShowExitScanner(false);
                setCarNumber('');
                toggleModal();
              }}
            > X </button>
            <h2 id="qr-label" style={{ width: "90%" }} >QR kodni skaner qiling</h2>
            <div id="qr-reader" className="qr-reader"></div>
            <h2 style={{ marginTop: 10, width: "90%" }} >Yoki moshina raqami/ID bilan qidiring</h2>
            <input
              type="text"
              placeholder="Moshina raqami yoki ID ni kiriting"
              style={{ width: '90%', marginBottom: '10px' }}
              value={carNumber}
              onChange={(e) => setCarNumber(e.target.value)}
              ref={inputRef}
            />
            <div style={{ maxHeight: '600px', overflowY: 'auto', scrollbarWidth: 'none', width: '90%', marginBottom: '10px' }}>
              {vehicles
                .filter(v => v.status === 'Turibti' && (
                  !carNumber || 
                  v.vehicle_id.toLowerCase().includes(carNumber.toLowerCase()) || 
                  (v.number && v.number.toLowerCase().includes(carNumber.toLowerCase()))
                ))
                .map(v => (
                  <div 
                    key={v.id} 
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #ccc',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onClick={() => handleScan(v.vehicle_id, true)}
                  >
                    <span>{v.number || 'Raqamsiz'} - {v.type}</span>
                    <span>{moment(v.entry_date).format('DD.MM.YYYY HH:mm')}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>ID: {v.vehicle_id}</span>
                  </div>
                ))}
            </div>
            <div className="modal-buttons">
              <button
                className="cancel"
                onClick={() => {
                  setShowExitScanner(false);
                  setCarNumber('');
                  toggleModal();
                }}
              >
                Bekor qilish
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && paymentDetails && (
        <div style={{ padding: 5, backgroundColor: '#000000aa', position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, zIndex: 11 }}>
          <div className="modal payment-modal">
            <h2>To'lov tafsilotlari</h2>
            <div className="payment-details">
              <p><strong>Moshina raqami:</strong> {paymentDetails.number}</p>
              <p><strong>ID:</strong> {paymentDetails.vehicleId}</p>
              <div style={{ display: 'flex', justifyContent: 'space-evenly', gap: 10, }} >
                <p><strong>Kommentariy kiriting</strong></p>
                <button onClick={() => {
                    const nextIsPayed = !isPayed;
                    setIsPayed(nextIsPayed);
                    if (!nextIsPayed) {
                      // Pul bermadi bosildi: faqat kirish uchun narx hisoblanadi,
                      // turgan kunlar price hisobiga kiritilmaydi
                      const { price: entryOnlyPrice } = calculatePrice(
                        paymentDetails.type,
                        paymentDetails.entryDate,
                        paymentDetails.entryDate
                      );
                      setPaymentDetails(prev => ({ ...prev, price: entryOnlyPrice }));
                    } else {
                      // Qaytadan "to'ladi" holatiga qaytarilganda to'liq narx tiklanadi
                      const { price: fullPrice } = calculatePrice(
                        paymentDetails.type,
                        paymentDetails.entryDate,
                        null
                      );
                      setPaymentDetails(prev => ({ ...prev, price: fullPrice }));
                    }
                  }}
                  style={{
                    background: isPayed ? 'white' : '#e53e3e',
                    border: isPayed ? '1px solid black' : 'none',
                    color: isPayed ? 'black' : 'white',
                    alignSelf: 'end',
                    fontSize: '0.9em',
                    padding: 10,
                    fontWeight: 'normal'
                  }}>
                  Pul bermadi
                </button>
              </div>
              <input 
                type="text" 
                value={paymentDetails.comment} 
                onChange={(e) => setPaymentDetails({...paymentDetails, comment: e.target.value})} 
                placeholder="Kommentariy kiritng" 
                style={{ width: '100%', padding: '10px', marginBottom: '10px', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-evenly', gap: 10, }} >
                <p><strong>Turi:</strong> {paymentDetails.type}</p>
                <p><strong>Turgan kunlar:</strong> {paymentDetails.days}</p>
              </div>
              <p className="payment-amount"><strong>To'lov summasi:</strong> {paymentDetails.price} so'm</p>
            </div>
            <div className="modal-buttons">
              <button onClick={() => {confirmPayment(); toggleModal();}} disabled={isLoading}>
                {isLoading ? 'Tasdiqlanmoqda...' : 'Tasdiqlash'}
              </button>
              <button className="cancel" onClick={() => {
                setShowPaymentModal(false);
                setPaymentDetails(null); toggleModal();
              }}>Bekor qilish</button>
            </div>
          </div>
        </div>
      )}

      {showPrintPrompt && (
        <div style={{ padding: 5, backgroundColor: '#000000aa', position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, zIndex: 11 }}>
          <div className="modal">
            <h2>Chek chiqarishni hohlaysizmi?</h2>
            <div className="modal-buttons">
              <button onClick={() => handlePrintDecision(true)}>Chiqarish</button>
              <button className="cancel" onClick={() => handlePrintDecision(false)}>Yo'q</button>
            </div>
          </div>
        </div>
      )}

      {(!showAddNumberModal && selectedVehicle) && (
        <VehicleDetails 
          vehicle={selectedVehicle} 
          toggleBody={toggleModal} 
          onClose={() => {setSelectedVehicle(null); }} 
          printReceipt={printReceipt} 
          imageFullScreen={() => setShowFullScreenImage(selectedVehicle.photo_url)}
        />
      )}

      {showFullScreenImage && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: '#000000', 
          zIndex: 19000, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          <img 
            src={showFullScreenImage} 
            alt="Fullscreen Vehicle" 
            style={{ maxWidth: '100%', maxHeight: '90%', objectFit: 'contain' }} 
          />
          <button 
            onClick={() => setShowFullScreenImage(null)} 
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '30px'
            }}
          >
            <IoClose />
          </button>
        </div>
      )}

      {showAddNumberModal && (
        <div style={{ padding: 5, backgroundColor: '#000000aa', position: 'fixed', top: 0, left: 0, bottom: 0, right: 0, zIndex: 11 }}>
          <div className="modal" style={{ overflowY: 'scroll', maxHeight: '90%', scrollbarWidth: 'none' }}>
            <h2>Raqam qo'shish</h2>
            {selectedVehicle.photo_url && (
              <img 
                src={selectedVehicle.photo_url} 
                alt="Foto" 
                style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain', cursor: 'pointer' }} 
                onClick={() => setShowFullScreenImage(selectedVehicle.photo_url)}
              />
            )}
            <input value={newNumber} onChange={e => setNewNumber(e.target.value)} placeholder="Moshina raqami" />
            <div className="modal-buttons">
              <button onClick={() => {setSelectedVehicle(null); addNumber();}} disabled={isLoading}>
                {isLoading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
              <button className="cancel" onClick={() => {
                setSelectedVehicle(null); 
                setShowAddNumberModal(false); 
              }}>Bekor qilish</button>
            </div>
          </div>
        </div>
      )}

      {showScrollTopButton && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '30px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            padding: 15,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            zIndex: 5,
          }}
          title="Yuqoriga"
        >
          <IoArrowUp size={30} />
        </button>
      )}
    </div>
  );
}

export default MainScreen;